import { session_id } from './session_id';
import { assertSudoWritable } from './sudoState';

// Same ceiling API.fetch uses, so the two paths fail on the same schedule.
const REQUEST_TIMEOUT_MS = 120_000;

export type OAuthScope = { scope: string; label: string };

export type PendingAuthorization = {
  client_name: string;
  redirect_host: string;
  workspace_name: string;
  scopes: OAuthScope[];
};

export type Grant = {
  id: number;
  /** The application this grant belongs to. Several grants can share one: re-consenting adds a grant. */
  client_id: string;
  client_name: string;
  scopes: string[];
  created_at: string;
};

/** The pending authorization request is gone: consumed, or past its 10 min TTL. */
export class AuthorizationExpiredError extends Error {
  constructor(message = 'Authorization request expired') {
    super(message);
    this.name = 'AuthorizationExpiredError';
    Object.setPrototypeOf(this, AuthorizationExpiredError.prototype);
  }
}

/** The bearer token the SPA holds is no longer a valid dashboard session. */
export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
    Object.setPrototypeOf(this, SessionExpiredError.prototype);
  }
}

/** The workspace is not entitled to `oauth:access`, so no consent can be shown. */
export class McpAccessDeniedError extends Error {
  constructor(message = 'Workspace is not enabled for MCP access') {
    super(message);
    this.name = 'McpAccessDeniedError';
    Object.setPrototypeOf(this, McpAccessDeniedError.prototype);
  }
}

/**
 * Deliberately NOT `API.fetch`.
 *
 * `API.fetch` treats every 401/403 as an expired session: it wipes the token from
 * localStorage and hard-navigates to '/'. On the OAuth surface a 403 is a *business*
 * answer — "this workspace has no oauth:access" — and the user's dashboard session is
 * perfectly valid. Routing it through `API.fetch` would log the user out and replace the
 * terminal state the consent screen is required to render with a silent redirect.
 *
 * The header construction below mirrors `API.fetch` exactly (bearer token, X-Request-ID,
 * workspace-id) so requests are indistinguishable server-side.
 */
async function oauthFetch(
  method: 'GET' | 'POST' | 'DELETE',
  url: string,
  data?: unknown,
): Promise<Response> {
  // Same rule as API.fetch: a tab whose sudo was switched elsewhere must not
  // post a consent decision or revoke a grant into the other tab's workspace.
  // Throws SudoStaleError; GET is still allowed.
  assertSudoWritable(method);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': session_id,
  };

  const token = localStorage.getItem('token');
  if (token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const workspace_id = localStorage.getItem('workspace_id');
  if (workspace_id !== null) {
    headers['workspace-id'] = workspace_id;
  }

  // RequestInit rather than a hand-written shape: the hand-written one omitted `signal`,
  // which TypeScript rejects — and the project's own type check could not report it,
  // because tsconfig requires TS 5+ (`moduleResolution: bundler`) while package.json pins
  // ^4.9.4, so compilation aborts on unparsable type deps before it reaches src/.
  const init: RequestInit = {
    method,
    headers,
    // Matches API.fetch's 120s ceiling. Without it a hung request never settles, the
    // consent screen's loading flag never clears, and the user sits on a spinner forever
    // with no terminal state — the one outcome this page is not allowed to have.
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  };

  if (data !== undefined) {
    init.body = JSON.stringify(data);
  }

  return fetch(url, init);
}

async function errorDetail(response: Response): Promise<string | null> {
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    return null;
  } catch {
    return null;
  }
}

async function throwForStatus(response: Response): Promise<never> {
  const detail = await errorDetail(response);

  if (response.status === 404) {
    throw new AuthorizationExpiredError(detail ?? undefined);
  }
  if (response.status === 401) {
    throw new SessionExpiredError(detail ?? undefined);
  }
  if (response.status === 403) {
    throw new McpAccessDeniedError(detail ?? undefined);
  }

  throw new Error(detail ?? `Request failed with status ${response.status}`);
}

export async function getPendingAuthorization(requestId: string): Promise<PendingAuthorization> {
  // `requestId` is opaque: it is only ever passed straight back to the API.
  const response = await oauthFetch('GET', `/API/v1/oauth/authorize/${encodeURIComponent(requestId)}`);

  if (!response.ok) {
    await throwForStatus(response);
  }

  return (await response.json()) as PendingAuthorization;
}

export async function postAuthorizationDecision(
  requestId: string,
  approve: boolean,
  scopes: string[],
): Promise<{ redirect_url: string }> {
  const response = await oauthFetch('POST', '/API/v1/oauth/authorize/decision', {
    request_id: requestId,
    approve,
    scopes,
  });

  if (!response.ok) {
    await throwForStatus(response);
  }

  return (await response.json()) as { redirect_url: string };
}

export async function listGrants(): Promise<Grant[]> {
  const response = await oauthFetch('GET', '/API/v1/oauth/grants');

  if (!response.ok) {
    await throwForStatus(response);
  }

  return (await response.json()) as Grant[];
}

export async function revokeGrant(id: number): Promise<void> {
  const response = await oauthFetch('DELETE', `/API/v1/oauth/grants/${id}`);

  if (!response.ok) {
    await throwForStatus(response);
  }
}
